import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { useDateFormatter } from '../hooks/useDateFormatter';
import DateTimePickerInput from './DateTimePickerInput';
import { hasActualTextContent } from '../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface CreateProblemFromIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  incident: any;
  onSuccess?: (problemNumber: string, incidentNumber: string) => void; // Optional callback when problem is created
}

// Impact options for Combobox (backend expects: high_1, medium_2, low_3)
const impactOptions = [
  { id: 'high_1', name: '1 - High' },
  { id: 'medium_2', name: '2 - Medium' },
  { id: 'low_3', name: '3 - Low' }
];

// Urgency options for Combobox (backend expects: high_1, medium_2, low_3)
const urgencyOptions = [
  { id: 'high_1', name: '1 - High' },
  { id: 'medium_2', name: '2 - Medium' },
  { id: 'low_3', name: '3 - Low' }
];

// Helper function to get local date in YYYY-MM-DD format
const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CreateProblemFromIncidentModal: React.FC<CreateProblemFromIncidentModalProps> = ({
  isOpen,
  onClose,
  incident,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { getToday } = useDateFormatter();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isRichTextEmpty = (content: string): boolean => !hasActualTextContent(content);

  const [formData, setFormData] = useState({
    contact: '',
    impact: '',
    urgency: '',
    priority: '',
    problemDate: getLocalDateString(),
    problemStatement: '',
    customerId: '',
    engagementId: '',
    resourceId: '',
    resourceIdValue: '',
    resourceName: '', // Store original resource name for display when resources haven't loaded
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: ''
  });
  const [isOtherResource, setIsOtherResource] = useState(false);

  // Search query states for Combobox fields
  const [contactQuery, setContactQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements'],
    queryFn: async () => {
      const response = await api.get('/engagements');
      return response.data;
    }
  });

  // Fetch assignment groups (only groups with type 'assignment')
  const { data: groupsData } = useQuery({
    queryKey: ['groups', 'assignment'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment');
      return response.data;
    }
  });

  // Fetch resources based on engagement
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return [];
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!formData.engagementId
  });

  // Fetch users based on assignment group
  const { data: usersData } = useQuery({
    queryKey: ['group-users', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch contacts for Contact field (filtered by customer and engagement)
  const { data: contactsData } = useQuery({
    queryKey: ['contacts-dropdown', formData.customerId, formData.engagementId],
    queryFn: async () => {
      // Prefer engagement filter if available, otherwise use customer filter
      if (formData.engagementId) {
        const response = await api.get(`/contacts/dropdown?engagementId=${formData.engagementId}`);
        return response.data;
      } else if (formData.customerId) {
        const response = await api.get(`/contacts/dropdown?customerId=${formData.customerId}`);
        return response.data;
      }
      return { contacts: [] };
    },
    enabled: !!formData.customerId || !!formData.engagementId
  });

  // Fetch primary contact for auto-selection
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Priority calculation based on Impact and Urgency
  const calculatePriority = (impact: string, urgency: string): string => {
    // Priority Matrix:
    // Impact\Urgency | High | Medium | Low
    // High           | 1    | 2      | 3
    // Medium         | 2    | 3      | 4
    // Low            | 3    | 4      | 5

    const matrix: Record<string, Record<string, string>> = {
      'high_1': { 'high_1': 'critical_1', 'medium_2': 'high_2', 'low_3': 'moderate_3' },
      'medium_2': { 'high_1': 'high_2', 'medium_2': 'moderate_3', 'low_3': 'low_4' },
      'low_3': { 'high_1': 'moderate_3', 'medium_2': 'low_4', 'low_3': 'planning_5' }
    };

    return matrix[impact]?.[urgency] || 'moderate_3';
  };

  // Auto-calculate priority when impact or urgency changes
  useEffect(() => {
    if (formData.impact && formData.urgency) {
      const calculatedPriority = calculatePriority(formData.impact, formData.urgency);
      setFormData(prev => ({ ...prev, priority: calculatedPriority }));
    }
  }, [formData.impact, formData.urgency]);

  // Update Resource ID value when resource changes
  useEffect(() => {
    if (formData.resourceId && resourcesData) {
      const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];
      const selectedResource = resourcesArray.find((r: any) => r.id === formData.resourceId);
      if (selectedResource) {
        setFormData(prev => ({ ...prev, resourceIdValue: selectedResource.resourceId || '' }));
      }
    }
  }, [formData.resourceId, resourcesData]);

  // Auto-select primary contact when customer/engagement changes
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contact) {
      setFormData(prev => ({
        ...prev,
        contact: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contact]);

  // Pre-fill form when incident data changes
  useEffect(() => {
    if (incident && isOpen) {
      // Check if incident has otherResourceNames (custom resources)
      const hasOtherResources = incident.otherResourceNames && incident.otherResourceNames.trim();
      setIsOtherResource(!!hasOtherResources);

      setFormData({
        contact: incident.contact?.id || '',
        impact: 'low_3',
        urgency: 'low_3',
        priority: 'planning_5', // Low + Low = Planning
        problemDate: getLocalDateString(),
        problemStatement: incident.description || '',
        customerId: incident.customerId || incident.Customer?.id || '',
        engagementId: incident.engagementId || incident.Engagement?.id || '',
        resourceId: hasOtherResources ? '' : (incident.resourceId || incident.Resource?.id || ''),
        resourceIdValue: hasOtherResources ? '' : (incident.resourceIdValue || incident.Resource?.resourceId || ''),
        resourceName: hasOtherResources ? '' : (incident.Resource?.name || ''),
        otherResourceNames: incident.otherResourceNames || '',
        assignmentGroupId: incident.assignmentGroupId || incident.assignmentGroup?.id || '',
        assignedTo: ''
      });
      setErrorMessage(null);
      setValidationErrors({});
    }
  }, [incident, isOpen]);

  const createProblemMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/problems', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Create related record linking the problem to the incident
      try {
        await api.post(`/cases/${data.problem.id}/related-records`, {
          relationships: [{
            targetId: incident.id,
            relationshipType: 'RELATED_TO'
          }],
          source: 'create_problem_from_incident'
        });
      } catch (error) {
        console.error('Failed to create related record:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['problems'] });
      queryClient.invalidateQueries({ queryKey: ['related-records', incident.id] });
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data.problem.number || data.problem.id, incident.number || incident.id);
      }
      
      onClose();
      // Navigate to the new problem
      navigate(`/problems/${data.problem.id}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to create problem';
      setErrorMessage(errorMsg);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const errors: Record<string, string> = {};

    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    if (!formData.contact) {
      errors.contact = 'Contact is required';
    } else {
      // Also validate that the selected contact exists in the available contacts list
      const contactExists = contacts.find((c: any) => c.id === formData.contact);
      if (!contactExists) {
        errors.contact = 'Contact is required';
      }
    }
    if (!formData.impact) {
      errors.impact = 'Impact is required';
    }
    if (!formData.urgency) {
      errors.urgency = 'Urgency is required';
    }
    if (!formData.problemDate) {
      errors.problemDate = 'Problem Date is required';
    } else {
      const selectedDateStr = formData.problemDate.split('T')[0];
      const todayStr = getToday();
      if (selectedDateStr > todayStr) {
        errors.problemDate = 'Problem Date cannot be a future date';
      }
    }
    if (isRichTextEmpty(formData.problemStatement)) {
      errors.problemStatement = 'Problem Statement is required';
    }
    if (!formData.assignmentGroupId) {
      errors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setValidationErrors({});
    setErrorMessage(null);

    const payload: any = {
      shortDescription: `Problem from ${incident.number}`,
      description: formData.problemStatement,
      priority: formData.priority,
      impact: formData.impact,
      urgency: formData.urgency,
      contactId: formData.contact, // Use contactId for Problems (not callerId)
      assignedTo: formData.assignedTo,
      assignmentGroupId: formData.assignmentGroupId || undefined,
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      problemDate: formData.problemDate,
      // Track the source incident for activity logging
      createdFromIncidentId: incident.id,
      createdFromIncidentNumber: incident.number
    };

    // Handle resource: either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      payload.otherResourceNames = formData.otherResourceNames.trim();
    } else if (formData.resourceId) {
      payload.resourceId = formData.resourceId;
      payload.resourceIdValue = formData.resourceIdValue || undefined;
    }

    createProblemMutation.mutate(payload);
  };

  const customers = customersData?.customers || [];
  const allEngagements = engagementsData?.engagements || [];

  // Filter engagements based on selected customer
  const engagements = formData.customerId
    ? allEngagements.filter((e: any) => e.Customer?.id === formData.customerId)
    : allEngagements;

  // Filter to only show groups with type 'assignment'
  const groups = (groupsData?.groups || []).filter((g: any) => g.type === 'assignment');
  const resources = Array.isArray(resourcesData) ? resourcesData : [];
  const groupUsers = usersData || [];
  const contacts = contactsData?.contacts || [];

  // Filtered lists for Combobox search
  const filteredContacts = contactQuery === ''
    ? contacts
    : contacts.filter((contact: any) => {
        const displayName = contact.firstName && contact.lastName
          ? `${contact.firstName} ${contact.lastName} - ${contact.email}`
          : contact.email;
        return displayName.toLowerCase().includes(contactQuery.toLowerCase());
      });

  const filteredCustomers = customerQuery === ''
    ? customers
    : customers.filter((customer: any) =>
        customer.accountName?.toLowerCase().includes(customerQuery.toLowerCase())
      );

  const filteredEngagements = engagementQuery === ''
    ? engagements
    : engagements.filter((engagement: any) => {
        return engagement.name?.toLowerCase().includes(engagementQuery.toLowerCase());
      });

  const filteredResources = resourceQuery === ''
    ? resources
    : resources.filter((resource: any) => {
        const displayName = resource.name || resource.resourceId || '';
        return displayName.toLowerCase().includes(resourceQuery.toLowerCase());
      });

  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((group: any) =>
        group.name?.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const filteredGroupUsers = assignedToQuery === ''
    ? groupUsers
    : groupUsers.filter((user: any) => {
        const displayName = user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email;
        return displayName.toLowerCase().includes(assignedToQuery.toLowerCase());
      });

  const filteredImpactOptions = impactQuery === ''
    ? impactOptions
    : impactOptions.filter((option) =>
        option.name.toLowerCase().includes(impactQuery.toLowerCase())
      );

  const filteredUrgencyOptions = urgencyQuery === ''
    ? urgencyOptions
    : urgencyOptions.filter((option) =>
        option.name.toLowerCase().includes(urgencyQuery.toLowerCase())
      );

  // Helper functions to get selected items
  const getSelectedContact = () => contacts.find((c: any) => c.id === formData.contact);
  const getSelectedCustomer = () => customers.find((c: any) => c.id === formData.customerId);
  const getSelectedEngagement = () => engagements.find((e: any) => e.id === formData.engagementId);
  const getSelectedResource = () => resources.find((r: any) => r.id === formData.resourceId);
  const getSelectedGroup = () => groups.find((g: any) => g.id === formData.assignmentGroupId);
  const getSelectedUser = () => groupUsers.find((u: any) => u.id === formData.assignedTo);
  const getSelectedImpact = () => impactOptions.find((o) => o.id === formData.impact);
  const getSelectedUrgency = () => urgencyOptions.find((o) => o.id === formData.urgency);

  // Helper function to get priority label
  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      'critical_1': 'Critical',
      'high_2': 'High',
      'moderate_3': 'Moderate',
      'low_4': 'Low',
      'planning_5': 'Planning'
    };
    return labels[priority] || 'Moderate';
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Create Problem from Incident {incident?.number}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
                  {/* Error Message */}
                  {errorMessage && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errorMessage}</div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Row 1: Customer and Engagement */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Customer */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Customer <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedCustomer() || null}
                            onChange={(customer: any) => {
                              setFormData({ ...formData, customerId: customer?.id || '', engagementId: '', resourceId: '', resourceIdValue: '', resourceName: '', otherResourceNames: '', contact: '' });
                              setCustomerQuery('');
                              setContactQuery('');
                              setIsOtherResource(false);
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border ${validationErrors.customerId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(customer: any) => customer?.accountName || ''}
                                onChange={(e) => setCustomerQuery(e.target.value)}
                                placeholder="Search customer..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredCustomers.length === 0 && customerQuery !== '' ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                    No customers found.
                                  </div>
                                ) : (
                                  filteredCustomers.map((customer: any) => (
                                    <Combobox.Option
                                      key={customer.id}
                                      value={customer}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {customer.accountName}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.customerId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.customerId}</p>
                          )}
                        </div>
                      </div>

                      {/* Engagement */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Engagement <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedEngagement() || null}
                            onChange={(engagement: any) => {
                              setFormData({ ...formData, engagementId: engagement?.id || '', resourceId: '', resourceIdValue: '', resourceName: '', otherResourceNames: '', contact: '' });
                              setEngagementQuery('');
                              setContactQuery('');
                              setIsOtherResource(false);
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border ${validationErrors.engagementId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(engagement: any) => engagement?.name || ''}
                                onChange={(e) => setEngagementQuery(e.target.value)}
                                placeholder="Search engagement..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredEngagements.length === 0 && engagementQuery !== '' ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                    No engagements found.
                                  </div>
                                ) : (
                                  filteredEngagements.map((engagement: any) => (
                                    <Combobox.Option
                                      key={engagement.id}
                                      value={engagement}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {engagement.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.engagementId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.engagementId}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Contact (full width on left side) */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Contact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          {(formData.customerId || formData.engagementId) ? (
                            <Combobox
                              value={getSelectedContact() || null}
                              onChange={(contact: any) => {
                                setFormData({ ...formData, contact: contact?.id || '' });
                                setContactQuery('');
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className={`block w-full rounded-md border ${validationErrors.contact ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                  displayValue={(contact: any) => contact?.name || ''}
                                  onChange={(e) => setContactQuery(e.target.value)}
                                  placeholder="Select customer first"
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredContacts.length === 0 && contactQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No contacts found.
                                    </div>
                                  ) : filteredContacts.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No contacts available for this customer/engagement.
                                    </div>
                                  ) : (
                                    filteredContacts.map((contact: any) => (
                                      <Combobox.Option
                                        key={contact.id}
                                        value={contact}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {contact.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select Customer or Engagement first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                          {validationErrors.contact && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.contact}</p>
                          )}
                        </div>
                      </div>
                      {/* Empty column for alignment */}
                      <div></div>
                    </div>

                    {/* Row 3: Impact and Urgency */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Impact */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Impact <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedImpact() || null}
                            onChange={(option: any) => {
                              setFormData({ ...formData, impact: option?.id || '' });
                              setImpactQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border ${validationErrors.impact ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(option: any) => option?.name || ''}
                                onChange={(e) => setImpactQuery(e.target.value)}
                                placeholder="Select impact..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredImpactOptions.map((option) => (
                                  <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.impact && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.impact}</p>
                          )}
                        </div>
                      </div>

                      {/* Urgency */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Urgency <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedUrgency() || null}
                            onChange={(option: any) => {
                              setFormData({ ...formData, urgency: option?.id || '' });
                              setUrgencyQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border ${validationErrors.urgency ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(option: any) => option?.name || ''}
                                onChange={(e) => setUrgencyQuery(e.target.value)}
                                placeholder="Select urgency..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredUrgencyOptions.map((option) => (
                                  <Combobox.Option
                                    key={option.id}
                                    value={option}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {option.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.urgency && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.urgency}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Priority and Problem Date */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Priority (Auto-calculated - readonly) */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Priority
                        </label>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={formData.priority ? getPriorityLabel(formData.priority) : ''}
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            placeholder="Auto-calculated from Impact & Urgency"
                          />
                        </div>
                      </div>

                      {/* Problem Date */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Problem Date <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <DateTimePickerInput
                            value={formData.problemDate}
                            max={getToday()}
                            onChange={(value) => setFormData({ ...formData, problemDate: value })}
                            showTimeSelect={false}
                            error={!!validationErrors.problemDate}
                          />
                          {validationErrors.problemDate && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.problemDate}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Problem Statement (full width) */}
                    <div className="flex items-start gap-3">
                      <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                        Problem Statement <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <div className={validationErrors.problemStatement ? 'border border-red-500 rounded-md' : ''}>
                          <RichTextEditor
                            value={formData.problemStatement}
                            onChange={(value) => {
                              setFormData({ ...formData, problemStatement: value });
                              if (validationErrors.problemStatement) {
                                setValidationErrors(prev => ({ ...prev, problemStatement: '' }));
                              }
                            }}
                            placeholder="Describe the problem statement..."
                            minHeight="120px"
                          />
                        </div>
                        {validationErrors.problemStatement && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.problemStatement}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 6: Resource and Resource ID */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resource */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Resource
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={isOtherResource ? '__OTHER__' : (getSelectedResource() || null)}
                            onChange={(value: any) => {
                              if (value === '__OTHER__') {
                                // User selected "Others"
                                setIsOtherResource(true);
                                setFormData({ ...formData, resourceId: '', resourceIdValue: '', resourceName: '', otherResourceNames: '' });
                              } else {
                                // User selected a normal resource
                                setIsOtherResource(false);
                                setFormData({ ...formData, resourceId: value?.id || '', resourceName: value?.name || '', otherResourceNames: '' });
                              }
                              setResourceQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                displayValue={(value: any) => {
                                  if (isOtherResource || value === '__OTHER__') return 'Others (specify custom resources)';
                                  // Use formData.resourceName as fallback when resources haven't loaded yet
                                  return value?.name || value?.resourceId || formData.resourceName || '';
                                }}
                                onChange={(e) => setResourceQuery(e.target.value)}
                                placeholder="Search resource..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredResources.length === 0 && resourceQuery !== '' ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                    No resources found.
                                  </div>
                                ) : (
                                  filteredResources.map((resource: any) => (
                                    <Combobox.Option
                                      key={resource.id}
                                      value={resource}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {resource.name || resource.resourceId}
                                          </span>
                                          {selected && !isOtherResource && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                                {/* Others option - always shown at the end */}
                                <Combobox.Option
                                  key="__OTHER__"
                                  value="__OTHER__"
                                  className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 border-t border-gray-200 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active }) => (
                                    <>
                                      <span className={`block truncate ${isOtherResource ? 'font-semibold' : 'font-normal'}`}>
                                        Others (specify custom resources)
                                      </span>
                                      {isOtherResource && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              </Combobox.Options>
                            </div>
                          </Combobox>
                        </div>
                      </div>

                      {/* Resource ID (readonly) or Other Resource Names input */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          {isOtherResource ? 'Resource Names' : 'Resource ID'}
                        </label>
                        <div className="flex-1">
                          {isOtherResource ? (
                            <div>
                              <input
                                type="text"
                                value={formData.otherResourceNames}
                                onChange={(e) => setFormData({ ...formData, otherResourceNames: e.target.value })}
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 sm:text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Enter resource names (comma separated)"
                              />
                              <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={formData.resourceIdValue}
                              disabled
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                              placeholder="Auto-filled from Resource"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 7: Assignment Group and Assigned To */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Assignment Group */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                        <div className="flex-1">
                          <Combobox
                            value={getSelectedGroup() || null}
                            onChange={(group: any) => {
                              setFormData({ ...formData, assignmentGroupId: group?.id || '', assignedTo: '' });
                              setGroupQuery('');
                              setAssignedToQuery('');
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`block w-full rounded-md border ${validationErrors.assignmentGroupId ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                displayValue={(group: any) => group?.name || ''}
                                onChange={(e) => setGroupQuery(e.target.value)}
                                placeholder="Search group..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredGroups.length === 0 && groupQuery !== '' ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                    No groups found.
                                  </div>
                                ) : (
                                  filteredGroups.map((group: any) => (
                                    <Combobox.Option
                                      key={group.id}
                                      value={group}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {group.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </div>
                          </Combobox>
                          {validationErrors.assignmentGroupId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignmentGroupId}</p>
                          )}
                        </div>
                      </div>

                      {/* Assigned To */}
                      <div className="flex items-start gap-3">
                        <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                          Assigned To
                        </label>
                        <div className="flex-1">
                          {formData.assignmentGroupId ? (
                            <Combobox
                              value={getSelectedUser() || null}
                              onChange={(user: any) => {
                                setFormData({ ...formData, assignedTo: user?.id || '' });
                                setAssignedToQuery('');
                              }}
                            >
                              <div className="relative">
                                <Combobox.Input
                                  className={`block w-full rounded-md border ${validationErrors.assignedTo ? 'border-red-500' : 'border-gray-300'} bg-white shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                                  displayValue={(user: any) => user ? (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email) : ''}
                                  onChange={(e) => setAssignedToQuery(e.target.value)}
                                  placeholder="Select Assigned To"
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredGroupUsers.length === 0 && assignedToQuery !== '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No users found.
                                    </div>
                                  ) : (
                                    filteredGroupUsers.map((user: any) => (
                                      <Combobox.Option
                                        key={user.id}
                                        value={user}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </div>
                            </Combobox>
                          ) : (
                            <input
                              type="text"
                              disabled
                              value=""
                              placeholder="Select Assignment Group first"
                              className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                            />
                          )}
                          {validationErrors.assignedTo && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignedTo}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createProblemMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                    >
                      {createProblemMutation.isPending ? 'Creating...' : 'Create Problem'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateProblemFromIncidentModal;
